import { promptInput, type SettingsRow } from '@bn-games/ui-shell';
import {
  DEFAULT_COLORS, DIFFICULTIES, LANGUAGES, THEME_NAMES,
  type DefaultColor, type Difficulty, type Language, type Settings, type ThemeName,
} from '../settings/types.js';
import { t } from '../i18n/I18n.js';
import { MAX_NAME_LENGTH } from '../settings/SettingsManager.js';
import { CLOCK_PRESETS, type ClockPreset } from '../game/clock.js';

/** Minimal contract the row builder needs — the menu context and the pause menu both satisfy it. */
export interface SettingsRowCtx {
  getSettings(): Settings;
  updateSettings(partial: Partial<Settings>): void;
}

export interface BuildSettingsRowsOptions {
  readonly ctx: SettingsRowCtx;
  /** The pause menu wraps game-config changes with confirm + restart. */
  readonly onGameConfigChange?: (apply: () => void) => void;
  /** Disable game-config rows entirely (online matches). */
  readonly disableGameConfig?: boolean;
}

function toggle(ctx: SettingsRowCtx, key: 'sound' | 'showValidMoves' | 'showMoveList' | 'flipBoard' | 'afkAssistEnabled' | 'afkRemindersEnabled' | 'turnStartSound' | 'showAfkPingButton', tab: 'gameplay' | 'interface' | 'audio'): SettingsRow {
  const value = ctx.getSettings()[key];
  return { kind: 'toggle', key, label: t(`settings.rows.${key}`), value, onClick: () => ctx.updateSettings({ [key]: !value }), tab };
}

export function buildSettingsRows(opts: BuildSettingsRowsOptions): readonly SettingsRow[] {
  const { ctx, onGameConfigChange, disableGameConfig = false } = opts;
  const s = ctx.getSettings();
  const applyGameConfig = (partial: Partial<Settings>): void => {
    const apply = (): void => ctx.updateSettings(partial);
    if (onGameConfigChange) onGameConfigChange(apply);
    else apply();
  };

  return [
    {
      kind: 'text',
      key: 'playerName',
      label: t('settings.rows.playerName'),
      value: s.playerName,
      onClick: () => {
        void promptInput({ title: t('settings.namePrompt'), defaultValue: s.playerName }).then((v) => {
          const trimmed = v?.trim().slice(0, MAX_NAME_LENGTH) ?? '';
          if (trimmed) ctx.updateSettings({ playerName: trimmed });
        });
      },
      tab: 'interface',
    },
    toggle(ctx, 'sound', 'audio'),
    {
      kind: 'selector',
      key: 'difficulty',
      label: t('settings.rows.difficulty'),
      value: s.difficulty,
      options: DIFFICULTIES,
      format: (v) => t(`settings.difficulties.${v as Difficulty}`),
      onChange: (next) => applyGameConfig({ difficulty: next as Difficulty }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'defaultColor',
      label: t('settings.rows.defaultColor'),
      value: s.defaultColor,
      options: DEFAULT_COLORS,
      format: (v) => t(`settings.colors.${v as DefaultColor}`),
      onChange: (next) => applyGameConfig({ defaultColor: next as DefaultColor }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'theme',
      label: t('settings.rows.theme'),
      value: s.theme,
      options: THEME_NAMES,
      format: (v) => t(`settings.themes.${v as ThemeName}`),
      onChange: (next) => ctx.updateSettings({ theme: next as ThemeName }),
      tab: 'interface',
    },
    {
      kind: 'selector',
      key: 'language',
      label: t('settings.rows.language'),
      value: s.language,
      options: LANGUAGES,
      format: (v) => t(`settings.languages.${v as Language}`),
      onChange: (next) => ctx.updateSettings({ language: next as Language }),
      tab: 'interface',
    },
    {
      kind: 'toggle',
      key: 'clockEnabled',
      label: t('settings.rows.clockEnabled'),
      value: s.clockEnabled,
      onClick: () => applyGameConfig({ clockEnabled: !s.clockEnabled }),
      disabled: disableGameConfig,
      tab: 'gameplay',
    },
    {
      kind: 'selector',
      key: 'clockPreset',
      label: t('settings.rows.clockPreset'),
      value: s.clockPreset,
      options: CLOCK_PRESETS,
      format: (v) => t(`settings.clockPresets.${v as ClockPreset}`),
      onChange: (next) => applyGameConfig({ clockPreset: next as ClockPreset }),
      disabled: disableGameConfig || !s.clockEnabled,
      tab: 'gameplay',
    },
    toggle(ctx, 'showValidMoves', 'interface'),
    toggle(ctx, 'showMoveList', 'interface'),
    toggle(ctx, 'flipBoard', 'interface'),
    toggle(ctx, 'afkAssistEnabled', 'gameplay'),
    toggle(ctx, 'afkRemindersEnabled', 'gameplay'),
    toggle(ctx, 'turnStartSound', 'gameplay'),
    toggle(ctx, 'showAfkPingButton', 'gameplay'),
  ];
}
